/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import { baseApiService } from './base'

const baseURL = '/api/metadatatemplates'
const baseURL_v1 = '/api/v1/metadatatemplates'
const baseURL_v2 = '/api/v2/metadatatemplates'

export interface AllOptionsForPicker {
  id: number
  name: string
}

export interface MetadataTemplate {
  id: number
  name: string
  type: string
  definition?: {
    options?: any[]
  }
}

export interface MetadataResponse {
  elements: MetadataTemplate[]
  totalElements: number
  totalPages: number
  pageNumber: number
  pageSize: number
}

export async function getAll(params?: Record<string, any>): Promise<MetadataResponse> {
  return baseApiService.fetch(baseURL, { params })
}

export async function getAllForPicker(params?: Record<string, any>): Promise<AllOptionsForPicker[]> {
  return new Promise<AllOptionsForPicker[]>((resolve, reject) => {
    baseApiService.fetch(baseURL + '/all', { params }).then((data) => {
      const localData = data as AllOptionsForPicker[]
      if (localData?.length) {
        localData.sort((prev, next) => prev.name.toLowerCase().localeCompare(next.name.toLowerCase()))
        resolve(localData)
      } else {
        reject()
      }
    })
  })
}

export const getOneProperty = (metadataId: number) => {
  return baseApiService.fetch(`${baseURL}/${metadataId}`, {
    method: 'GET'
  })
}

export const getArtifactValues = (metadataId: number, fsiId: number) => {
  return baseApiService.fetch(`${baseURL_v1}/${metadataId}/fsi/${fsiId}`, {
    method: 'GET'
  })
}

export const getById = (id: number) => {
  return baseApiService.fetch(`${baseURL}/${id}`, {
    method: 'GET'
  })
}

export const getPropertyUsageMetadata = (metadataId: number, hideLoader?: boolean) => {
  return baseApiService.fetch(`${baseURL}/${metadataId}/usage`, {
    method: 'GET'
  })
}

export const getAvailableTypes = (translateFn?: (key: string) => string) => {
  const types = [
    { opt: 'date', id: 'DATETIME', value: translateFn ? translateFn('METADATA_TYPE_DATETIME') : 'Date/Time' },
    { opt: 'text', id: 'STRING', value: translateFn ? translateFn('METADATA_TYPE_STRING') : 'Text' },
    { opt: 'number', id: 'NUMBER', value: translateFn ? translateFn('METADATA_TYPE_NUMBER') : 'Number' },
    {
      opt: 'multi-pick-list',
      id: 'SELECT_MULTI',
      value: translateFn ? translateFn('METADATA_TYPE_SELECT_MULTI') : 'Multi Select'
    },
    {
      opt: 'pick-list',
      id: 'SELECT_ONE',
      value: translateFn ? translateFn('METADATA_TYPE_SELECT_ONE') : 'Single Select'
    },
    {
      opt: 'pick-list-user',
      id: 'SELECT_ONE_USER',
      value: translateFn ? translateFn('METADATA_TYPE_SELECT_ONE_USER') : 'Single User Select'
    },
    {
      opt: 'multi-pick-list-user',
      id: 'SELECT_MULTI_USER',
      value: translateFn ? translateFn('METADATA_TYPE_SELECT_MULTI_USER') : 'Multi User Select'
    },
    {
      opt: 'pick-list-group',
      id: 'SELECT_ONE_GROUP',
      value: translateFn ? translateFn('METADATA_TYPE_SELECT_ONE_GROUP') : 'Single Group Select'
    },
    {
      opt: 'multi-pick-list-group',
      id: 'SELECT_MULTI_GROUP',
      value: translateFn ? translateFn('METADATA_TYPE_SELECT_MULTI_GROUP') : 'Multi Group Select'
    }
  ]

  // Add artifact type (matching original logic)
  types.push({
    opt: 'pick-list-artifact',
    id: 'ARTIFACT',
    value: translateFn ? translateFn('METADATA_TYPE_ARTIFACT') : 'Artifact'
  })

  // Sort by value (matching original logic)
  types.sort((a, b) => a.value.localeCompare(b.value))

  return Promise.resolve(types)
}

export const exportPropertiesReports = async (filter: any, meta: any): Promise<void> => {
  const requestObj = { filter, meta }
  return baseApiService.fetch('/api/reports/propertiesusageexport', {
    method: 'POST',
    body: requestObj
  })
}

export const useMetadataApi = () => {
  return {
    getAll,
    getOneProperty,
    getArtifactValues,
    getAllForPicker,
    getById,
    getPropertyUsageMetadata,
    getAvailableTypes,
    exportPropertiesReports
  }
}
